import { type Ref, forwardRef } from 'react';
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form';

import getFieldError from '@/shared/form/helpers/getFieldError';
import useDisabledBy from '@/shared/form/hooks/useDisabledBy';
import useMapCustomValidators from '@/shared/form/hooks/useMapCustomValidators';
import type { InputProps } from '@/shared/form/types';

import { Textarea as MantineTextarea, type TextareaProps as MantineTextareaProps } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';

export type TextareaProps<T extends FieldValues> = Omit<InputProps<UseControllerProps<T>>, 'onKeyDown'> &
  Omit<MantineTextareaProps, 'value' | 'defaultValue' | 'onChange'>;

export default forwardRef(function Textarea<T extends FieldValues>(
  { name, control, defaultValue, shouldUnregister, onChange, ...props }: TextareaProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const rules = useMapCustomValidators(props, true);

  const {
    field: { value, onChange: fieldOnChange, ref: insideRef, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const options = useDisabledBy(props, {});
  if (props.disabled) {
    options.disabled = true;
  }

  const mergedRefs = useMergedRef(insideRef, ref);

  return (
    <MantineTextarea
      ref={mergedRefs}
      value={value}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(name, e.target.value);
      }}
      error={getFieldError(fieldState)}
      mb="md"
      data-testid={name}
      {...field}
      {...props}
      {...options}
    />
  );
});
